// Rate sheet names for equipment on client-facing documents.
//
// The client signs and pays against the rate sheet, so a daily field ticket,
// a week summary and an invoice name a piece of equipment the way the rate
// sheet does ("Truck - 3/4 Ton") — never by the JDE tier ("PU-3/4T",
// "Truck-1 1/2T Roust w/Autocrane") or by whatever HeavyJob or the rental PO
// calls it ("Bare Rate", "120-TON RT CRANE"). The unit number still prints
// beside it, so two trucks of one kind stay two rows.
//
// A unit resolves in this order:
//   1. unit number -> JDE tier -> the rate sheet row that tier bills at
//      (billingPricing equipBillsOf: the job's saved map, else the Rates &
//      Rules suggestion). This is what the Billing tab prices with, so the
//      ticket and the invoice name the unit alike.
//   2. the description is already a rate sheet item name (a reviewed time
//      card where project controls typed it that way)
//   3. the description rules below — the field's own words for a unit
//   4. a rental's PO line item (PDLITM, what the unit is: "120-TON RT CRANE"),
//      when the caller has it (rentalItems) — HeavyJob only has the line's
//      Description 1, often a rate label ("Bare Rate")
// A unit none of them names keeps its description and is reported, so it can
// be mapped rather than quietly printed under the wrong name.
//
// No imports, no JSX: the packet scripts in localserver/scripts load this file
// as text (evalModule), the same way they load billingDailyLabor.js.

const norm = (s) => String(s || '').toLowerCase().replace(/&amp;/g, '&').replace(/[^a-z0-9]+/g, ' ').trim();
export const normUnit = (s) => String(s || '').toUpperCase().replace(/\s+/g, '').trim();

// Field / HeavyJob / PO description -> rate sheet item name. null: not a rate
// sheet item (nothing to rename it to). Started from the billing draft's
// table, which priced the 8/26-9/20 invoices.
export const EQUIP_DESC_RULES = [
    [/Torque Wrench/i, null],
    [/Labor for tool trailer/i, null],
    [/4 Gas Monitor|4-GAS-MON|Gas Monitor/i, '4 Gas Monitor'],
    [/Bus - 10-19|15 Pass/i, 'Bus - 15 Passenger Van'],
    [/Truck - 1\/2 Ton/i, 'Truck - 1/2 Ton'],
    [/Truck - 3\/4 Ton/i, 'Truck - 3/4 Ton'],
    [/Roust w\/Autocrane/i, 'Truck - 1.5 Ton w/Autocrane'],
    [/Enclosed Cargo Trailer|Encl\. Tool/i, "Trailer - 12' - 24' Enclosed Tool Trailer"],
    [/Gooseneck/i, 'Trailer - Gooseneck Tandem Axle'],
    [/Honda 6500W/i, 'Generator - 5.0 to 7.5 KW'],
    [/12000# 53'-69'/i, 'Forklift - Telehandler - 10k to 12k lb Cap.'],
    [/37-40' Elect Scissor/i, "Man lift - 40' - 54' Scissor Lift"],
    [/40-50' Articulating Boom/i, "Man lift - 45' - 59' - Articulating Boom Lift"],
    [/Light Tower|LED Vertical Mast/i, 'Light Plant - 6.0 - 7.5 KW'],
    [/53' Van Trailers/i, "Tool Van - 40' - 53' Trailer"],
    [/185CFM/i, 'Air Compressors - 185 CFM'],
    [/Welder ARC 300AMP/i, 'Welding Machine - 300 Amp'],
    [/120-Ton RT Crane/i, 'Crane - 101-130 Ton Rough Terrain'],
    [/Boom 80' Articulating/i, "Man lift - 80' - Articulating Boom Lift"],
    [/135' Articulating|135' Manlift/i, "Man lift - 135' Lift"],
    // The 8/31 sheet's Cathodic Protection row. PB4's SO has no such row and
    // bills these as "ATV - 4 Wheel" (billingContract CONTRACTS.pb4.equipAs).
    [/4\/6 Passenger UTV/i, 'UTV / 4x4 side by side / 8x8 amphibious'],
];

// Descriptions that say exactly which machine a unit is, so on the Overview
// they outrank the JDE tier: rentals coded under the tier of another machine
// (a 245 trackhoe on DZ-280-W, a 600-ton crawler on CR-101-110, the 135' and
// 80' booms on ML-BL-85FT) and rentals whose tier has no rate sheet row
// (GN-20KW, TL-WT-500G, TK-10Y-MIX) (Seth 10/29: the Overview's equipment
// titles "don't match the equipment rate sheet for block 4"). The two cranes
// are bigger than anything Attachment B lists, so they print as the machine.
// The time card workbook's own names for tools and small equipment follow
// (Seth 10/29: "map them"). Billing still prices by tier.
export const MACHINE_RULES = [
    [/24[56] Trackhoe/i, 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)'],
    [/600-?Ton Crawler/i, 'Crane - 600 Ton Crawler'],
    [/160T RT Crane/i, 'Crane - 160 Ton Rough Terrain'],
    [/Manlift Boom 135'|135' Articulating|135' Manlift|Articulating Boom 135'/i, "Man lift - 135' Lift"],
    [/80' Articulating/i, "Man lift - 80' - Articulating Boom Lift"],
    [/40-50' Articulating Boom/i, "Man lift - 45' - 59' - Articulating Boom Lift"],
    [/Walk[- ]?Behind Trench Roller/i, 'Trench Roller Compactor'],
    [/Generator.{0,3}25 ?(Kw|Kva)/i, 'Generator - 25kva'],
    [/Mortar Mixer|Cement Mixer/i, 'Portable Cement Mixer'],
    [/Water Trailer/i, 'Trailer - Water'],
    [/40' - 54' Scissor/i, "Man lift - 40' - 54' Scissor Lift"],
    [/185 ?(cm|cfm)/i, 'Air Compressors - 185 CFM'],
    [/6500W Honda|Honda 6500W/i, 'Generator - 5.0 to 7.5 KW'],
    [/Gang\s*Box/i, '36x60 Gang Box'],
    [/Welding Machines? 300 ?amp|Welder ARC 300 ?AMP/i, 'Welding Machine - 300 Amp'],
    [/Ridgid 535|Rigid 535|535 Rigid/i, '535 Rigid Threader 1/2"- 2"'],
    [/Ridgid 1224|Rigid 1224|1224 Rigid/i, '1224 Rigid Threader 1/2"- 4"'],
    // Greenlee 854: the electric bender for 1/2" to 2".
    [/Greenlee\s+854/i, 'Electric Conduit Bender 1/2" to 2"'],
    [/53' (Tool Van|Van Trailers?) w\/ ?Tools/i, "Tool Van - 40' - 53' Trailer"],
];
export const machineName = (desc) => {
    const hit = MACHINE_RULES.find(([re]) => re.test(String(desc || '')));
    return hit ? hit[1] : '';
};

/**
 * A rate sheet item as the job's Service Order bills it: `equipAs` is the
 * contract's swap (billingContract equipRowAs — PB4 bills the 8/31 sheet's
 * Cathodic Protection UTV row as its "ATV - 4 Wheel"). A swap whose row is
 * not on the job's schedule is ignored.
 */
const rowAs = (name, equipAs, rows) => {
    const to = name && equipAs ? equipAs[name] : '';
    if (!to) return name;
    if (!(rows || []).length) return to;
    const row = rows.find((r) => norm(r.name) === norm(to));
    return row ? row.name : name;
};

/**
 * The rate sheet item a description names, else ''. `equipRows` is the job's
 * equipment schedule ([{ name }]); a rule only answers with a name that is
 * on it, so a row renamed on this job is not printed under its old name.
 * `equipAs` (optional) is the job's Service Order swap, see rowAs.
 */
export const sheetNameFromDesc = (desc, equipRows, equipAs) => {
    const d = String(desc || '').replace(/\s*\(Rental\)\s*$/i, '').trim();
    if (!d) return '';
    const rows = equipRows || [];
    const k = norm(d);
    const exact = rows.find((r) => norm(r.name) === k);
    if (exact) return rowAs(exact.name, equipAs, rows);
    const hit = EQUIP_DESC_RULES.find(([re]) => re.test(d));
    if (!hit || !hit[1]) return '';
    if (!rows.length) return rowAs(hit[1], equipAs, rows);
    const row = rows.find((r) => norm(r.name) === norm(hit[1]));
    return row ? rowAs(row.name, equipAs, rows) : '';
};

/**
 * Unit number -> JDE tier code, off the job's cost rows (fleet 52100 and
 * rental 52200) and its rental usage ([{ code: 'PO-231801-1', tier }]).
 */
export const unitTiersOf = (costRows, rentalUsage) => {
    const out = {};
    (costRows || []).forEach((r) => {
        const ct = String(r?.Cost_Type_Code || '');
        if (ct !== '52100' && ct !== '52200') return;
        const unit = normUnit(r.Unit_Number);
        const tier = String(r.Equipment_Classification_Tier_Name || r.Rental_Equipment_Classification_Tier_Name || '').trim();
        if (unit && tier && !out[unit]) out[unit] = tier;
    });
    (rentalUsage || []).forEach((u) => {
        const unit = normUnit(u?.code || u?.unit);
        const tier = String(u?.tier || '').trim();
        if (unit && tier && !out[unit]) out[unit] = tier;
    });
    return out;
};

// A rental PO line's Description 1 that is a rate label, not a machine.
const RATE_LABEL_TAIL = /\s*-?\s*bare\s+(rate|rental)\s*$/i;

/**
 * A rental by its PO line, as the ticket scripts' rentalLabel prints it: the
 * item (PDLITM) and the line's description (PDDSC1, a spec) - the description
 * alone when it already names the item, the item alone when the description
 * is only a rate label ("Bare Rate", "Bare Rental").
 */
export const rentalLabel = (item, desc) => {
    const it = String(item || '').trim();
    const d = String(desc || '').replace(/\s*\(Rental\)\s*$/i, '').replace(RATE_LABEL_TAIL, '').trim();
    if (!it) return d;
    if (!d) return it;
    if (norm(d).includes(norm(it))) return d;
    return `${it} - ${d}`;
};

/**
 * Unit number -> the rental PO line's item, off the job's rental usage
 * ([{ code: 'PO-231534-1', item }]).
 */
export const rentalItemsOf = (rentalUsage) => {
    const out = {};
    (rentalUsage || []).forEach((u) => {
        const unit = normUnit(u?.code || u?.unit);
        const item = String(u?.item || '').trim();
        if (unit && item && !out[unit]) out[unit] = item;
    });
    return out;
};

/**
 * (unit, desc) => { name, via } — `via` is 'tier' | 'sheet' | 'rule' | 'po',
 * or '' with name '' when nothing names the unit.
 *   unitTiers    unitTiersOf(...)
 *   equipBills   { <tier code>: { name } } — billingPricing equipBillsOf
 *   equipRows    the job's equipment schedule rows
 *   rentalItems  rentalItemsOf(...), optional: the last resort for a rental
 *   equipAs      billingContract equipRowAs(job), optional: rows the job's
 *                Service Order bills as another row. The tier step needs none
 *                — equipBills already says what the tier prices at.
 */
export const equipNamer = ({ unitTiers = {}, equipBills = {}, equipRows = [], rentalItems = {}, equipAs = {} } = {}) => (unit, desc) => {
    const tier = unitTiers[normUnit(unit)];
    const byTier = tier && equipBills[tier] && equipBills[tier].name;
    if (byTier) return { name: byTier, via: 'tier' };
    const d = String(desc || '').replace(/\s*\(Rental\)\s*$/i, '').trim();
    const k = norm(d);
    if (k && (equipRows || []).some((r) => norm(r.name) === k)) return { name: sheetNameFromDesc(d, equipRows, equipAs), via: 'sheet' };
    const byRule = sheetNameFromDesc(d, equipRows, equipAs);
    if (byRule) return { name: byRule, via: 'rule' };
    const item = rentalItems[normUnit(unit)];
    return item ? { name: rentalLabel(item, d), via: 'po' } : { name: '', via: '' };
};

/**
 * Equipment rows renamed for print: `desc` becomes the rate sheet item, the
 * text it replaced is kept as `src`, and `unnamed` lists the units nothing
 * could name (they keep their description).
 */
export const nameEquipment = (equipment, namer) => {
    const unnamed = [];
    const rows = (equipment || []).map((e) => {
        const raw = e.src !== undefined ? e.src : e.desc;
        const hit = typeof namer === 'function' ? namer(e.unit, raw) : { name: '' };
        if (!hit.name) { unnamed.push({ unit: e.unit || '', desc: raw || '' }); return { ...e, desc: raw, src: raw }; }
        return { ...e, desc: hit.name, src: raw, via: hit.via };
    });
    return { equipment: rows, unnamed };
};

/**
 * The Equipment cell of an invoice line: unit number and rate sheet item,
 * never the JDE tier code. `t` is a priced Billing line ({ unit, tier, ba,
 * eq, who }); a line with no rate sheet item prints its unit alone.
 */
export const clientEquipLabel = (t, equipBills) => {
    const name = String(t?.ba || (t?.tier && equipBills?.[t.tier]?.name) || '').trim();
    const unit = String(t?.unit || '').trim();
    return [unit, name].filter(Boolean).join(' - ') || String(t?.who || '');
};
